import axios, { type AxiosError } from 'axios';

/**
 * Shape of the standardized error response from the backend.
 * See: oikosEstats/apps/api/src/common/filters/http-exception.filter.ts
 */
export interface ApiErrorResponse {
  code: string;
  message: string | string[];
  details?: Record<string, string[]> | string;
  timestamp: string;
  path: string;
}

/**
 * Type guard, checks if an unknown error is an AxiosError carrying our backend error shape.
 */
export function isApiError(error: unknown): error is AxiosError<ApiErrorResponse> {
  return axios.isAxiosError(error) && typeof error.response?.data?.code === 'string';
}

/**
 * Extract a human-readable error message from any error.
 *
 * Priority:
 *   1. Backend structured message (string or joined array)
 *   2. Axios generic message (e.g. "Network Error")
 *   3. Plain Error.message
 *   4. Provided fallback
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (!error || typeof error !== 'object') return fallback;

  if (axios.isAxiosError(error)) {
    const msg = error.response?.data?.message;
    if (Array.isArray(msg)) return msg.join('. ');
    if (typeof msg === 'string') return msg;
    if (error.message) return error.message;
  }

  if (error instanceof Error) return error.message;

  // AuthError shape thrown by useAuthStore
  if ('message' in error && typeof (error as { message: unknown }).message === 'string') {
    return (error as { message: string }).message;
  }

  return fallback;
}

/**
 * Extract the backend error code (e.g. "ConflictException", "TWO_FACTOR_REQUIRED").
 * Returns null for non-API errors.
 */
export function getErrorCode(error: unknown): string | null {
  if (isApiError(error)) {
    return error.response?.data?.code ?? null;
  }
  // AuthError shape thrown by useAuthStore
  if (error && typeof error === 'object' && 'code' in error) {
    const code = (error as { code: unknown }).code;
    return typeof code === 'string' ? code : null;
  }
  return null;
}

/**
 * Extract per-field validation errors from a backend 400 response.
 * Returns null if the error doesn't contain field-level details.
 *
 * Example backend response:
 *   { details: { email: ["must be an email"], name: ["too short"] } }
 */
export function getFieldErrors(error: unknown): Record<string, string> | null {
  if (!isApiError(error)) return null;

  const details = error.response?.data?.details;
  if (!details || typeof details !== 'object' || Array.isArray(details)) return null;

  const fieldErrors: Record<string, string> = {};
  for (const [field, messages] of Object.entries(details)) {
    fieldErrors[field] = Array.isArray(messages) ? messages[0] : String(messages);
  }
  return Object.keys(fieldErrors).length > 0 ? fieldErrors : null;
}

/**
 * Check if an error is a specific HTTP status code.
 */
export function isHttpStatus(error: unknown, status: number): boolean {
  return axios.isAxiosError(error) && error.response?.status === status;
}
